import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Globe,
  Key,
  Copy,
  Check,
  TrendingUp,
  Clock,
  CheckCircle,
  X,
  RefreshCw,
  Eye,
} from "lucide-react";
import Swal from "sweetalert2";

const API_BASE = process.env.REACT_APP_API_URL;

export default function GestionB2CPage({ onBack }) {
  const [margeB2C, setMargeB2C] = useState(15);
  const [inputMarge, setInputMarge] = useState(15);
  const [apiKey, setApiKey] = useState("");
  const [copied, setCopied] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedResa, setSelectedResa] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const handleOpenDetails = async (r) => {
    setSelectedResa(r);
    setShowDetails(true);
    setDetailsData(null);
    setIsLoadingDetails(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(
        `${API_BASE}/api/admin/b2c/reservations/${r.id_resa}/details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setDetailsData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    fetchReservations();
  }, []);

  const fetchConfig = async () => {
    const token = localStorage.getItem("authToken");
    const res = await fetch(`${API_BASE}/api/admin/b2c/apikey`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setApiKey(data.api_key || "");
    setMargeB2C(parseFloat(data.marge_b2c) || 15);
    setInputMarge(parseFloat(data.marge_b2c) || 15);
  };

  const fetchReservations = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE}/api/admin/b2c/reservations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setReservations(data.reservations || []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveMarge = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE}/api/admin/b2c/marge`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ marge_b2c: inputMarge }),
      });
      if (!res.ok) throw new Error("Erreur lors de la sauvegarde");
      setMargeB2C(inputMarge);
      Swal.fire({
        icon: "success",
        title: "Marge B2C mise à jour",
        text: `Nouvelle marge: ${inputMarge}%`,
        confirmButtonColor: "#FF7B47",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: err.message,
        confirmButtonColor: "#FF7B47",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirm = async (id) => {
    const result = await Swal.fire({
      icon: "question",
      title: "Confirmer cette réservation B2C ?",
      text: "Cette action va soumettre la réservation à MyGO.",
      showCancelButton: true,
      confirmButtonColor: "#22c55e",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Confirmer",
      cancelButtonText: "Annuler",
    });
    if (!result.isConfirmed) return;

    const token = localStorage.getItem("authToken");
    const res = await fetch(`${API_BASE}/api/reservations/${id}/confirm`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (data.success || data.mygoState) {
      fetchReservations();
    } else {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: data.error,
        confirmButtonColor: "#FF7B47",
      });
    }
  };

  const handleReject = async (id) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Rejeter cette réservation B2C ?",
      input: "textarea",
      inputPlaceholder: "Motif (optionnel)",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Rejeter",
      cancelButtonText: "Annuler",
    });
    if (!result.isConfirmed) return;
    const token = localStorage.getItem("authToken");
    await fetch(`${API_BASE}/api/reservations/${id}/reject`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ adminNotes: result.value }),
    });
    fetchReservations();
  };

  const copyKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const statusBadge = (status) => {
    const map = {
      "En Attente": { label: "En attente", color: "bg-orange-100 text-orange-700" },
      Valider: { label: "Confirmée", color: "bg-green-100 text-green-700" },
      Annuler: { label: "Annulée", color: "bg-red-100 text-red-700" },
      MyGO_En_Attente: { label: "MyGO pending", color: "bg-blue-100 text-blue-700" },
    };
    const s = map[status] || { label: status, color: "bg-gray-100 text-gray-700" };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${s.color}`}>
        {s.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-6xl">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[#2B4979] hover:text-[#FF7B47] font-semibold transition"
          >
            <ArrowLeft size={20} /> Retour
          </button>
          <h1 className="text-3xl font-bold text-[#2B4979]">Gestion B2C</h1>
        </div>

        {/* Section 1 — Marge B2C */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingUp size={24} className="text-[#FF7B47]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#2B4979]">Marge B2C</h2>
              <p className="text-sm text-gray-500">
                Appliquée sur les prix MyGO pour les clients particuliers
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Marge actuelle:{" "}
                <span className="text-[#FF7B47] text-lg">{margeB2C}%</span>
              </label>
              <input
                type="range"
                min="0"
                max="50"
                step="0.5"
                value={inputMarge}
                onChange={(e) => setInputMarge(parseFloat(e.target.value))}
                className="w-full accent-orange-500"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center border-2 border-gray-200 rounded-lg overflow-hidden">
                <input
                  type="number"
                  min="0"
                  max="50"
                  step="0.5"
                  value={inputMarge}
                  onChange={(e) => setInputMarge(parseFloat(e.target.value))}
                  className="w-24 px-3 py-2 text-center text-lg font-bold outline-none"
                />
                <span className="px-3 py-2 bg-gray-100 text-gray-600 font-semibold">%</span>
              </div>
              <button
                onClick={handleSaveMarge}
                disabled={isSaving || inputMarge === margeB2C}
                className="px-6 py-2 bg-[#FF7B47] hover:bg-[#ff6a30] disabled:opacity-50 text-white rounded-lg font-semibold transition"
              >
                {isSaving ? "Sauvegarde..." : "Sauvegarder"}
              </button>
            </div>
          </div>
        </div>

        {/* Section 2 — B2C Reservations */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe size={24} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#2B4979]">Réservations B2C</h2>
                <p className="text-sm text-gray-500">
                  Réservations en provenance de votre site B2C
                </p>
              </div>
            </div>
            <button
              onClick={fetchReservations}
              className="flex items-center gap-2 text-gray-500 hover:text-[#FF7B47] transition"
            >
              <RefreshCw size={18} /> Actualiser
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF7B47]"></div>
            </div>
          ) : reservations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Globe size={48} className="mx-auto mb-3 opacity-30" />
              <p>Aucune réservation B2C pour le moment</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#2B4979] text-white">
                  <tr>
                    {["Référence", "Client", "Hôtel", "Séjour", "Montant", "Statut", "Actions"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-sm font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reservations.map((r, idx) => (
                    <tr key={r.id_resa} className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                      <td className="px-4 py-3 font-mono text-sm font-semibold text-[#2B4979]">
                        {r.ref_commande}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-semibold">{r.client_prenom} {r.client_nom}</div>
                        <div className="text-xs text-gray-500">{r.client_mail}</div>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">{r.nom_hotel}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div>{new Date(r.date_arr).toLocaleDateString("fr-FR")}</div>
                        <div className="text-xs text-gray-400">
                          → {new Date(r.date_dep).toLocaleDateString("fr-FR")}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-[#2B4979]">
                        {parseFloat(r.montant_vente).toFixed(3)} TND
                      </td>
                      <td className="px-4 py-3">{statusBadge(r.etat_commande)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpenDetails(r)}
                            className="flex items-center gap-1 px-3 py-1 bg-[#2B4979] hover:bg-[#1e3454] text-white rounded-lg text-xs font-semibold transition"
                          >
                            <Eye size={14} /> Détails
                          </button>
                          {r.etat_commande === "En Attente" && (
                            <>
                              <button
                                onClick={() => handleConfirm(r.id_resa)}
                                className="flex items-center gap-1 px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-semibold transition"
                              >
                                <CheckCircle size={14} /> Confirmer
                              </button>
                              <button
                                onClick={() => handleReject(r.id_resa)}
                                className="flex items-center gap-1 px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-semibold transition"
                              >
                                <X size={14} /> Rejeter
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Details Modal ── */}
      {showDetails && selectedResa && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

            {/* Modal header */}
            <div className="flex items-center justify-between p-6 border-b bg-[#2B4979] text-white rounded-t-xl">
              <div>
                <h2 className="text-xl font-bold">Détails de la réservation B2C</h2>
                <p className="text-sm text-white/70 font-mono mt-1">{selectedResa.ref_commande}</p>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="hover:bg-white/20 p-2 rounded-full transition"
              >
                <X size={22} />
              </button>
            </div>

            {isLoadingDetails ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF7B47]"></div>
              </div>
            ) : (
              <div className="p-6 space-y-5">

                {/* Client info */}
                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Client</p>
                    <p className="font-semibold text-[#2B4979]">
                      {selectedResa.client_prenom} {selectedResa.client_nom}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm">{selectedResa.client_mail || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Téléphone</p>
                    <p className="text-sm">{selectedResa.client_tel || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Statut</p>
                    {statusBadge(selectedResa.etat_commande)}
                  </div>
                </div>

                {/* Hotel + dates */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-xs text-blue-600 font-semibold mb-1">Hôtel</p>
                    <p className="font-bold text-[#2B4979]">{selectedResa.nom_hotel}</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <p className="text-xs text-orange-600 font-semibold mb-1">Séjour</p>
                    <p className="font-semibold text-gray-700">
                      {new Date(selectedResa.date_arr).toLocaleDateString("fr-FR")}{" "}
                      {"→"}{" "}
                      {new Date(selectedResa.date_dep).toLocaleDateString("fr-FR")}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedResa.nb_adl} adulte(s)
                      {selectedResa.nb_enf > 0 ? `, ${selectedResa.nb_enf} enfant(s)` : ""}
                    </p>
                  </div>
                </div>

                {/* Rooms */}
                {detailsData?.mygoPayload?.rooms && detailsData.mygoPayload.rooms.length > 0 && (
                  <div>
                    <h3 className="font-bold text-[#2B4979] mb-3">Chambres</h3>
                    <div className="space-y-3">
                      {detailsData.mygoPayload.rooms.map((room, idx) => {

                        // Room name — try multiple key types since MyGO IDs can be string or number
                        const roomName =
                          detailsData.roomNames?.[room.mygoRoomId] ||
                          detailsData.roomNames?.[String(room.mygoRoomId)] ||
                          detailsData.roomNames?.[parseInt(room.mygoRoomId)] ||
                          room.roomName ||
                          `Chambre #${room.mygoRoomId}`;

                        // Boarding name — same type-safe lookup
                        const boardingName =
                          detailsData.boardingNames?.[room.mygoBoardingId] ||
                          detailsData.boardingNames?.[String(room.mygoBoardingId)] ||
                          detailsData.boardingNames?.[parseInt(room.mygoBoardingId)] ||
                          room.boardingName ||
                          `Boarding #${room.mygoBoardingId}`;

                        const adultsCount = Array.isArray(room.adults)
                          ? room.adults.length
                          : (room.adults || 0);

                        const childrenCount = Array.isArray(room.children)
                          ? room.children.length
                          : (parseInt(room.children) || 0);

                        return (
                          <div key={idx} className="border border-gray-200 rounded-lg p-4">

                            {/* Room title */}
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-bold text-[#2B4979]">
                                Chambre {idx + 1}: {roomName}
                              </span>
                              {room.onRequest && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded font-semibold">
                                  Sur demande
                                </span>
                              )}
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">

                              {/* Boarding */}
                              <div>
                                <span className="text-gray-400">Formule:</span>{" "}
                                <span className="font-medium">{boardingName}</span>
                              </div>

                              {/* Adults count */}
                              <div>
                                <span className="text-gray-400">Adultes:</span>{" "}
                                <span className="font-medium">{adultsCount}</span>
                              </div>

                              {/* Children count */}
                              {childrenCount > 0 && (
                                <div>
                                  <span className="text-gray-400">Enfants:</span>{" "}
                                  <span className="font-medium">{childrenCount}</span>
                                </div>
                              )}

                              {/* Views */}
                              {room.mygoViewIds?.length > 0 && (
                                <div>
                                  <span className="text-gray-400">Vue:</span>{" "}
                                  {room.mygoViewIds.join(", ")}
                                </div>
                              )}

                              {/* Supplements */}
                              {room.mygoSupplementIds?.length > 0 && (
                                <div>
                                  <span className="text-gray-400">Suppléments:</span>{" "}
                                  {room.mygoSupplementIds.join(", ")}
                                </div>
                              )}

                              {/* Adult passenger names */}
                              {Array.isArray(room.adults) && room.adults.some((a) => a.Name) && (
                                <div className="col-span-2 mt-2 space-y-1">
                                  <p className="text-xs text-gray-400 font-semibold">Passagers adultes:</p>
                                  {room.adults.map((a, i) => (
                                    <div
                                      key={i}
                                      className="text-xs text-gray-500 pl-2 border-l-2 border-blue-200"
                                    >
                                      {a.Name || `Passager ${i + 1}`}
                                      {a.Holder ? " ★ Titulaire" : ""}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Children passenger names */}
                              {Array.isArray(room.children) && room.children.length > 0 && (
                                <div className="col-span-2 mt-2 space-y-1">
                                  <p className="text-xs text-gray-400 font-semibold">Enfants:</p>
                                  {room.children.map((c, i) => (
                                    <div
                                      key={i}
                                      className="text-xs text-gray-500 pl-2 border-l-2 border-orange-200"
                                    >
                                      {c.Name || `Enfant ${i + 1}`} — {c.Age} ans
                                    </div>
                                  ))}
                                </div>
                              )}

                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Special options */}
                {detailsData?.mygoPayload?.optionIds && detailsData.mygoPayload.optionIds.length > 0 && (
                  <div>
                    <h3 className="font-bold text-[#2B4979] mb-2">Demandes spéciales</h3>
                    <div className="flex flex-wrap gap-2">
                      {detailsData.mygoPayload.optionIds.map((id) => (
                        <span
                          key={id}
                          className="bg-purple-100 text-purple-700 text-xs px-3 py-1 rounded-full font-semibold"
                        >
                          {detailsData.optionNames?.[id] || `Option #${id}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Observations */}
                {selectedResa.observation && (
                  <div>
                    <h3 className="font-bold text-[#2B4979] mb-2">Observations client</h3>
                    <p className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                      {selectedResa.observation}
                    </p>
                  </div>
                )}

                {/* Financials */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">Montant achat</p>
                    <p className="font-bold text-gray-700">
                      {parseFloat(selectedResa.montant_achat).toFixed(3)} TND
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-green-600">Montant vente</p>
                    <p className="font-bold text-green-700">
                      {parseFloat(selectedResa.montant_vente).toFixed(3)} TND
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-orange-600">Commission</p>
                    <p className="font-bold text-orange-700">
                      {parseFloat(selectedResa.commission).toFixed(3)} TND
                    </p>
                  </div>
                </div>

              </div>
            )}

            {/* Modal footer */}
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
              {selectedResa.etat_commande === "En Attente" && (
                <>
                  <button
                    onClick={() => { setShowDetails(false); handleConfirm(selectedResa.id_resa); }}
                    className="flex items-center gap-2 px-5 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition"
                  >
                    <CheckCircle size={16} /> Confirmer
                  </button>
                  <button
                    onClick={() => { setShowDetails(false); handleReject(selectedResa.id_resa); }}
                    className="flex items-center gap-2 px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition"
                  >
                    <X size={16} /> Rejeter
                  </button>
                </>
              )}
              <button
                onClick={() => setShowDetails(false)}
                className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition"
              >
                Fermer
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}